import { API } from '@crowdset/contracts/constants';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useCommand } from '../../room/context';
import { useRoomStore } from '../../room/store';
import { Button, Dialog, Field, Select, Textarea } from '../../ui/kit';
import { useRoomDialogs } from './dialogs-store';

const REASONS = [
  { value: 'spam', label: 'Spam or flooding' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'hate', label: 'Hate speech' },
  { value: 'sexual', label: 'Sexual content' },
  { value: 'violence', label: 'Violence or threats' },
  { value: 'other', label: 'Something else' },
];

/** All room-level dialogs, mounted once so menus can close before a dialog opens. */
export function RoomDialogs() {
  const dialog = useRoomDialogs((s) => s.dialog);
  const close = useRoomDialogs((s) => s.close);
  const onOpenChange = (o: boolean) => !o && close();
  if (!dialog) return null;
  switch (dialog.kind) {
    case 'report':
      return <ReportDialog key="report" onOpenChange={onOpenChange} />;
    case 'kick':
    case 'ban':
      return <ModDialog key={dialog.kind} onOpenChange={onOpenChange} />;
    case 'skip':
      return <SkipDialog key="skip" onOpenChange={onOpenChange} />;
  }
}

function ReportDialog({ onOpenChange }: { onOpenChange: (o: boolean) => void }) {
  const dialog = useRoomDialogs((s) => s.dialog) as Extract<
    NonNullable<ReturnType<typeof useRoomDialogs.getState>['dialog']>,
    { kind: 'report' }
  >;
  const roomId = useRoomStore((s) => s.data?.room.id)!;
  const [reason, setReason] = useState('spam');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const who = dialog.target?.displayName ?? dialog.message?.author.displayName ?? 'this user';
  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post(API.reports, {
        roomId,
        targetUserId: dialog.target?.userId ?? dialog.message?.author.id,
        chatMessageId: dialog.message?.id,
        reason,
        details,
      });
      useRoomStore.getState().toast('Report sent to the CrowdSet team. Thank you.', 'ok');
      onOpenChange(false);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      title={`Report ${who}`}
      description="Reports go to platform admins, not room moderators. The reported person is not told who reported them."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {dialog.message && (
          <blockquote className="rounded-xl border border-white/10 bg-ink-900 p-3 text-sm text-fg-muted">
            “{dialog.message.body}”
          </blockquote>
        )}
        <Field label="Reason">
          {(id) => <Select id={id} value={reason} onChange={setReason} options={REASONS} />}
        </Field>
        <Field label="Details" hint="Optional, up to 1000 characters.">
          {(id, d) => (
            <Textarea
              id={id}
              aria-describedby={d}
              maxLength={1000}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
            />
          )}
        </Field>
        {err && (
          <p role="alert" className="text-sm text-[#f3a3a4]">
            {err}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            Send report
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function ModDialog({ onOpenChange }: { onOpenChange: (o: boolean) => void }) {
  const dialog = useRoomDialogs((s) => s.dialog) as {
    kind: 'kick' | 'ban';
    target: { userId: string | null; participantId: string; displayName: string };
  };
  const kick = useCommand('moderation.kick', { success: 'Removed from the room.' });
  const ban = useCommand('moderation.ban', { success: 'Banned from the room.' });
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('1440');
  const isBan = dialog.kind === 'ban';
  const submit = async () => {
    const userId = dialog.target.userId;
    const ack = isBan
      ? await ban.run({
          userId: userId!,
          reason,
          minutes: duration === 'perm' ? null : Number(duration),
        })
      : userId
        ? await kick.run({ userId, reason })
        : await kick.run({ participantId: dialog.target.participantId, reason });
    if (ack.ok) onOpenChange(false);
  };
  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      title={`${isBan ? 'Ban' : 'Kick'} ${dialog.target.displayName}`}
      description={
        isBan
          ? 'A ban removes them now and blocks them from rejoining this room, across reconnects. It is recorded in the audit log.'
          : 'A kick removes them from the room now. They can come back. It is recorded in the audit log.'
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {isBan && (
          <Field label="Duration">
            {(id) => (
              <Select
                id={id}
                value={duration}
                onChange={setDuration}
                options={[
                  { value: '60', label: '1 hour' },
                  { value: '1440', label: '24 hours' },
                  { value: '10080', label: '7 days' },
                  { value: 'perm', label: 'Until lifted' },
                ]}
              />
            )}
          </Field>
        )}
        <Field
          label={isBan ? 'Reason (required)' : 'Reason'}
          hint="Visible to room moderators and admins."
        >
          {(id, d) => (
            <Textarea
              id={id}
              aria-describedby={d}
              required={isBan}
              minLength={isBan ? 3 : 0}
              maxLength={200}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="danger" loading={kick.pending || ban.pending}>
            {isBan ? 'Ban' : 'Kick'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function SkipDialog({ onOpenChange }: { onOpenChange: (o: boolean) => void }) {
  const play = useRoomStore((s) => s.data?.play ?? null);
  const skip = useCommand('moderation.skip', { success: 'Track skipped.' });
  const [reason, setReason] = useState('');
  if (!play) return null;
  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      title="Skip this track for everyone"
      description={`“${play.title}” played by ${play.dj.displayName}. The DJ stays in the rotation; use “Remove from DJ rotation” separately.`}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          const ack = await skip.run({ playId: play.playId, reason });
          if (ack.ok || ack.error.code === 'STALE') onOpenChange(false);
        }}
      >
        <Field label="Reason (required)" hint="Recorded in the audit log with this play.">
          {(id, d) => (
            <Textarea
              id={id}
              aria-describedby={d}
              required
              minLength={3}
              maxLength={200}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Off-topic for the room"
            />
          )}
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="danger"
            loading={skip.pending}
            disabled={reason.trim().length < 3}
          >
            Skip track
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
